<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';
import { ChevronDown } from 'lucide-vue-next';
import { AccordionContent, AccordionHeader, AccordionItem, AccordionRoot, AccordionTrigger } from 'reka-ui';

interface AccordionWrapperProperties {
  triggerButtonLabel: string;
  hasActiveFilters?: boolean;
  loading?: boolean;
}

withDefaults(defineProps<AccordionWrapperProperties>(), {
  hasActiveFilters: false,
  loading: false,
});

const openItems = ref(['filter']);
const contentReference = useTemplateRef<ComponentPublicInstance>('content');

watch(openItems, (value) => {
  if (!value.includes('filter')) return;
  void nextTick(() => {
    const element: unknown = contentReference.value?.$el;
    if (!(element instanceof HTMLElement)) return;
    const onEnd = () => {
      element.removeEventListener('animationend', onEnd);
      if (element.dataset.state === 'open') {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };
    element.addEventListener('animationend', onEnd);
  });
});
</script>

<template>
  <AccordionRoot
    v-model="openItems"
    type="multiple"
    class="w-full py-4"
    :aria-busy="loading"
  >
    <AccordionItem value="filter">
      <AccordionHeader class="flex items-center">
        <div
          v-if="loading"
          class="flex w-full justify-between gap-4"
        >
          <UiSkeleton class="h-3.5 w-24" />
          <UiSkeleton class="h-3.5 w-4" />
        </div>
        <AccordionTrigger
          v-else
          class="group flex flex-1 items-center justify-between text-sm font-semibold text-foreground transition-all [&[data-state=open]>svg]:rotate-180"
        >
          <span class="flex items-center gap-2 leading-none">
            {{ triggerButtonLabel }}
            <span
              v-if="hasActiveFilters"
              class="sr-only"
            >
              — {{ $t('components.results.filters.activeFilters') }}
            </span>
            <UiCustomSeparatorDot
              v-if="hasActiveFilters"
              color-class="bg-brand-primary-700"
              class="group-data-[state=open]:hidden"
            />
          </span>
          <ChevronDown class="h-4 w-4 shrink-0 text-foreground transition-transform" />
        </AccordionTrigger>
      </AccordionHeader>
      <AccordionContent
        ref="content"
        class="overflow-hidden py-3 text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      >
        <div class="flex flex-col gap-4">
          <slot />
        </div>
      </AccordionContent>
    </AccordionItem>
  </AccordionRoot>
</template>
